/**
 * 成本测算（Alice 页面 costAnalytics 的复刻）：上游成本、覆盖率、毛利与未知成本风险。
 *
 * 背景：用户扣费与上游成本在同一张用量表里，成本未知（upstream_cost 为空）不等于零成本。本页只把成本已知的请求
 *   计入金额，未知请求、Token 与对应用户扣费单列，覆盖率低于 95% 时所有金额按「下限」解读。
 * 设计意图：
 *   - 版式与 Alice 逐块对应：日期筛选置顶，三个标签页（经营总览 / 用户成本 / 模型与业务），表头可排序，可导出 CSV。
 *   - 金额从人民币换成美元，糖豆换成花瓣；「用户」换成「账号」，微信收款换成在线支付（扣除退款）。
 *   - 「内部使用」对应仅记账（metered）请求；仅记账请求在被标为「不走账」的渠道上成本记 0，毛利只比较扣费请求。
 *   - Alice 的「朋友圈 / 自主生活补贴」在本仓没有对应业务，今日经营按来源归类：对话（空、main、chat）、
 *     免费业务（非对话来源且扣费为 0）、其他；「日志未归因」档恒为空，有数据才显示。
 * 关键约束：日期范围最长 90 天（后端校验）；「今日经营结果」不随日期筛选变化；加载序号防止慢请求覆盖新结果。
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { bizApi, type CostAnalytics as CostData, type CostMetric } from '@ap/api-client/admin-biz'
import { UNITS_PER_USD, fmtInt } from '@ap/api-client/money'
import { A } from '@ap/i18n/admin'
import { BIZ, BIZ_HELP } from '@ap/i18n/admin-biz'
import { C, errorText } from '@ap/i18n/common'
import { intlTag, useI18n, type Entry } from '@ap/i18n'
import { Link, Notice, useLoad, useToast } from '@ap/ui'
import {
  ChartCanvas,
  FIELD,
  Help,
  SortTh,
  beijingDate,
  bizError,
  chartOpts,
  compactTokens,
  coverageColor,
  downloadCsv,
  ratePct,
  signColor,
  spanDays,
  useSorted,
  type SortState,
} from '../../alice/biz-kit.tsx'
import { OpsNote, OpsPage, Stat, Stats } from '../../alice/kit.tsx'
import { useUsd } from '../../shared.tsx'

const usdVal = (u: number) => u / UNITS_PER_USD

type Row = Record<string, unknown>
type Tab = 'overview' | 'users' | 'models'

interface Col {
  key: string
  label: Entry
  help?: string
  cell: (r: Row) => ReactNode
  csv?: (r: Row) => unknown
}

export function CostAnalytics() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const usd = useUsd()
  const toast = useToast()
  const [start, setStart] = useState(beijingDate(-6))
  const [end, setEnd] = useState(beijingDate(0))
  const [applied, setApplied] = useState({ start: beijingDate(-6), end: beijingDate(0) })
  const [tab, setTab] = useState<Tab>('overview')
  const load = useLoad<CostData>(() => bizApi.costAnalytics({ start_date: applied.start, end_date: applied.end }), [applied.start, applied.end])
  const d = load.data

  useEffect(() => {
    if (load.error) toast.error(bizError(load.error, locale))
  }, [load.error, toast, locale])

  const days = spanDays(applied.start, applied.end)
  const setRange = (n: number) => {
    const s = beijingDate(-(n - 1))
    const e = beijingDate(0)
    setStart(s)
    setEnd(e)
    setApplied({ start: s, end: e })
  }
  const query = () => {
    if (!start || !end) return setRange(7)
    if (start > end) return toast.error(t(BIZ.ca.badRange))
    setApplied({ start, end })
  }

  // 金额：小额保留 4 位，避免单次请求成本显示成 $0.00
  const m = (u: number | null | undefined) => usd(u ?? 0, Math.abs(u ?? 0) >= UNITS_PER_USD ? 2 : 4)
  const int = (n: number | null | undefined) => fmtInt(n ?? 0, tag)
  const H = (k: string) => <Help k={k} help={BIZ_HELP} locale={locale} closeLabel={t(C.close)} />

  const ext = d?.summary.external
  const intl = d?.summary.internal
  const coverage = ext?.cost_coverage ?? 0

  // ------------------------------------------------------------------ 表格数据
  const todayRows = useMemo<Row[]>(() => {
    if (!d) return []
    const tb = d.today_business
    const keys: (keyof typeof tb & string)[] = ['conversation', 'moments_subsidy', 'other']
    if (tb.unattributed.requests > 0) keys.push('unattributed')
    keys.push('overall')
    return keys.map((k) => ({ key: k, label: t(BIZ.ca.biz[k as keyof typeof BIZ.ca.biz]), ...(tb[k as 'overall'] as CostMetric) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, locale])
  const dailyRows = useMemo<Row[]>(
    () =>
      (d?.daily ?? []).map((r) => ({
        date: r.date,
        users: r.external.users,
        requests: r.external.requests,
        user_charge_units: r.external.user_charge_units,
        known_margin_units: r.external.known_margin_units,
        known_margin_rate: r.external.known_margin_rate,
        conversation_margin_units: r.business.conversation.known_margin_units,
        conversation_margin_rate: r.business.conversation.known_margin_rate,
        moments_subsidy_units: r.business.moments_subsidy.known_upstream_cost_units,
        business_attribution_rate: r.business_attribution_rate,
        known_upstream_cost_units: r.external.known_upstream_cost_units,
        internal_cost_units: r.internal.known_upstream_cost_units,
        cost_coverage: r.external.cost_coverage,
        unknown_cost_requests: r.external.unknown_cost_requests,
        unknown_tokens: r.external.unknown_tokens,
      })),
    [d],
  )
  const userRows = useMemo<Row[]>(() => (d?.top_users ?? []).map((r) => ({ ...r, tokens: r.input_tokens + r.output_tokens })), [d])
  const unknownRows = useMemo<Row[]>(() => (d?.unknown_risk_users ?? []).map((r) => ({ ...r })), [d])
  const modelRows = useMemo<Row[]>(() => (d?.models ?? []).map((r) => ({ ...r, tokens: r.input_tokens + r.output_tokens })), [d])
  const sourceRows = useMemo<Row[]>(
    () =>
      (d?.sources ?? []).map((r) => ({
        ...r,
        source_label: BIZ.ca.src[r.source as keyof typeof BIZ.ca.src] ? t(BIZ.ca.src[r.source as keyof typeof BIZ.ca.src]) : r.source,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [d, locale],
  )

  const sToday = useSorted(todayRows, { key: 'known_upstream_cost_units', dir: 'desc' }, locale)
  const sDaily = useSorted(dailyRows, { key: 'date', dir: 'desc' }, locale)
  const sUsers = useSorted(userRows, { key: 'known_upstream_cost_units', dir: 'desc' }, locale)
  const sUnknown = useSorted(unknownRows, { key: 'unknown_tokens', dir: 'desc' }, locale)
  const sModels = useSorted(modelRows, { key: 'known_upstream_cost_units', dir: 'desc' }, locale)
  const sSources = useSorted(sourceRows, { key: 'known_upstream_cost_units', dir: 'desc' }, locale)

  // ------------------------------------------------------------------ 列定义
  const num = (r: Row, k: string) => Number(r[k] ?? 0)
  const money = (key: string, label: Entry, help?: string, colored = false): Col => ({
    key,
    label,
    help,
    cell: (r) => (colored ? <span style={{ color: signColor(num(r, key)) }}>{m(num(r, key))}</span> : m(num(r, key))),
    csv: (r) => usdVal(num(r, key)),
  })
  const count = (key: string, label: Entry, help?: string): Col => ({ key, label, help, cell: (r) => int(num(r, key)) })
  const tokens = (key: string, label: Entry, help?: string): Col => ({ key, label, help, cell: (r) => compactTokens(num(r, key)), csv: (r) => num(r, key) })
  const rate = (key: string, label: Entry, help?: string, covColor = false): Col => ({
    key,
    label,
    help,
    cell: (r) => {
      const v = r[key] as number | null
      return covColor && v !== null && v !== undefined ? <span style={{ color: coverageColor(v) }}>{ratePct(v)}</span> : ratePct(v)
    },
    csv: (r) => (r[key] === null || r[key] === undefined ? '' : r[key]),
  })
  const orgCell = (r: Row): ReactNode => (
    <>
      <Link href={`/accounts/${r.account_id as string}`}>{r.name as string}</Link>
      <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}> ID {(r.account_id as string).slice(0, 8)}</span>
    </>
  )

  const todayCols: Col[] = [
    { key: 'label', label: BIZ.ca.colBiz, help: 'todayBusiness', cell: (r) => <strong>{r.label as string}</strong> },
    money('known_user_charge_units', BIZ.ca.colKnownCharge, 'margin'),
    money('known_upstream_cost_units', BIZ.ca.colKnownCost, 'knownCost'),
    money('known_margin_units', BIZ.ca.colMargin, 'margin', true),
    rate('known_margin_rate', BIZ.ca.colMarginRate, 'margin'),
    rate('cost_coverage', BIZ.ca.colCoverage, 'coverage', true),
  ]
  const dailyCols: Col[] = [
    { key: 'date', label: BIZ.ca.colDate, help: 'date', cell: (r) => r.date as string },
    count('users', BIZ.ca.colActiveOrgs, 'activeUsers'),
    count('requests', BIZ.ca.colRequests, 'requests'),
    money('user_charge_units', BIZ.ca.colCharge, 'userCharge'),
    money('known_margin_units', BIZ.ca.colOverallMargin, 'dailyOverallMargin', true),
    rate('known_margin_rate', BIZ.ca.colOverallRate, 'dailyOverallMargin'),
    money('conversation_margin_units', BIZ.ca.colChatMargin, 'dailyConversationMargin', true),
    rate('conversation_margin_rate', BIZ.ca.colChatRate, 'dailyConversationMargin'),
    money('moments_subsidy_units', BIZ.ca.colSubsidy, 'dailyMomentsSubsidy'),
    rate('business_attribution_rate', BIZ.ca.colAttribution, 'businessAttribution', true),
    money('known_upstream_cost_units', BIZ.ca.colKnownCost, 'knownCost'),
    money('internal_cost_units', BIZ.ca.colInternalCost, 'internalCost'),
    rate('cost_coverage', BIZ.ca.colCoverage, 'coverage', true),
    count('unknown_cost_requests', BIZ.ca.colUnknownReq, 'unknownRequests'),
    tokens('unknown_tokens', BIZ.ca.colUnknownTokens, 'unknownTokens'),
  ]
  const userCols: Col[] = [
    { key: 'name', label: BIZ.ca.colOrg, help: 'user', cell: orgCell },
    count('active_days', BIZ.ca.colActiveDays, 'activeDays'),
    count('requests', BIZ.ca.colRequests, 'requests'),
    tokens('tokens', BIZ.ca.colTokens, 'tokens'),
    money('period_paid_units', BIZ.ca.colPeriodPaid, 'periodPaid'),
    money('lifetime_paid_units', BIZ.ca.colLifetimePaid, 'lifetimePaid'),
    money('user_charge_units', BIZ.ca.colCharge, 'userCharge'),
    money('known_upstream_cost_units', BIZ.ca.colKnownCost, 'knownCost'),
    rate('cost_coverage', BIZ.ca.colCoverage, 'coverage', true),
    money('known_margin_units', BIZ.ca.colMargin, 'margin', true),
    money('free_known_cost_units', BIZ.ca.colFreeCost, 'freeCost'),
  ]
  const unknownCols: Col[] = [
    { key: 'name', label: BIZ.ca.colOrg, help: 'user', cell: orgCell },
    count('unknown_cost_requests', BIZ.ca.colUnknownReq, 'unknownRequests'),
    tokens('unknown_tokens', BIZ.ca.colUnknownTokens, 'unknownTokens'),
    money('unknown_user_charge_units', BIZ.ca.colUnknownCharge, 'unknownCharge'),
    money('known_upstream_cost_units', BIZ.ca.colKnownCost, 'knownCost'),
    rate('cost_coverage', BIZ.ca.colTotalCoverage, 'coverage', true),
  ]
  const modelCols: Col[] = [
    { key: 'model_id', label: BIZ.ca.colModel, help: 'model', cell: (r) => <span className="mono">{r.model_id as string}</span> },
    { key: 'provider_id', label: BIZ.ca.colProvider, help: 'provider', cell: (r) => <span className="mono">{r.provider_id as string}</span> },
    count('users', BIZ.ca.colOrgs, 'activeUsers'),
    count('requests', BIZ.ca.colRequests, 'requests'),
    tokens('tokens', BIZ.ca.colTokens, 'tokens'),
    money('user_charge_units', BIZ.ca.colModelCharge, 'modelUserCharge'),
    money('known_user_charge_units', BIZ.ca.colComparableCharge, 'modelComparableCharge'),
    money('known_upstream_cost_units', BIZ.ca.colSupplierCost, 'supplierCost'),
    money('known_margin_units', BIZ.ca.colMargin, 'margin', true),
    rate('known_margin_rate', BIZ.ca.colMarginRate, 'margin'),
    rate('cost_coverage', BIZ.ca.colCoverage, 'coverage', true),
    money('free_known_cost_units', BIZ.ca.colFreeCost, 'freeCost'),
    count('unknown_cost_requests', BIZ.ca.colUnknownReq, 'unknownRequests'),
    tokens('unknown_tokens', BIZ.ca.colUnknownTokens, 'unknownTokens'),
  ]
  const sourceCols: Col[] = [
    { key: 'source_label', label: BIZ.ca.colSource, help: 'source', cell: (r) => r.source_label as string },
    count('users', BIZ.ca.colOrgs, 'activeUsers'),
    count('requests', BIZ.ca.colRequests, 'requests'),
    money('known_upstream_cost_units', BIZ.ca.colKnownCost, 'knownCost'),
    rate('cost_coverage', BIZ.ca.colCoverage, 'coverage', true),
    money('free_known_cost_units', BIZ.ca.colFreeCost, 'freeCost'),
  ]

  const exportCsv = (name: string, cols: Col[], rows: Row[]) => {
    if (!rows.length) return toast.error(t(BIZ.ca.nothingToExport))
    downloadCsv(
      `${name}-${applied.start}_${applied.end}.csv`,
      cols.map((c) => ({ key: c.key, label: t(c.label) })),
      rows.map((r) => Object.fromEntries(cols.map((c) => [c.key, c.csv ? c.csv(r) : r[c.key]]))),
    )
  }

  const table = (cols: Col[], s: { sorted: Row[]; state: SortState; toggle: (k: string) => void }, rowKey: (r: Row) => string, emptyText: string) => (
    <div className="table-inner sticky-table">
      <table>
        <thead>
          <tr>
            {cols.map((c) => (
              <SortTh key={c.key} sortKey={c.key} state={s.state} onSort={s.toggle} help={c.help ? <> {H(c.help)}</> : undefined}>
                {t(c.label)}
              </SortTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {s.sorted.length === 0 ? (
            <tr>
              <td colSpan={cols.length} className="empty">
                {load.loading && !d ? t(BIZ.common.loading) : emptyText}
              </td>
            </tr>
          ) : (
            s.sorted.map((r) => (
              <tr key={rowKey(r)}>
                {cols.map((c) => (
                  <td key={c.key}>{c.cell(r)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )

  // ------------------------------------------------------------------ 图表
  const charts = useMemo(() => {
    if (!d) return null
    const labels = d.daily.map((r) => r.date.slice(5))
    const attributed = (rate: number) => rate >= 0.95
    const cost = chartOpts()
    const cov = chartOpts()
    cov.scales = {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { beginAtZero: true, max: 100, grid: { color: 'rgba(0,0,0,.04)' }, ticks: { font: { size: 10 }, callback: (v: unknown) => `${v}%` } },
    }
    return {
      cost: {
        type: 'bar' as const,
        data: {
          labels,
          datasets: [
            {
              label: t(BIZ.ca.chartChat),
              data: d.daily.map((r) => (attributed(r.business_attribution_rate) ? usdVal(r.business.conversation.known_margin_units) : null)),
              backgroundColor: 'rgba(37,99,235,.62)',
              borderRadius: 4,
            },
            {
              label: t(BIZ.ca.chartSubsidy),
              data: d.daily.map((r) => (attributed(r.business_attribution_rate) ? -usdVal(r.business.moments_subsidy.known_upstream_cost_units) : null)),
              backgroundColor: 'rgba(220,38,38,.58)',
              borderRadius: 4,
            },
            {
              label: t(BIZ.ca.chartOverall),
              data: d.daily.map((r) => usdVal(r.external.known_margin_units)),
              type: 'line' as const,
              borderColor: '#10b981',
              backgroundColor: 'transparent',
              pointRadius: 2,
              borderWidth: 2,
              tension: 0.25,
            },
          ],
        },
        options: cost,
      },
      coverage: {
        type: 'line' as const,
        data: {
          labels,
          datasets: [
            {
              label: t(BIZ.ca.chartCoverage),
              data: d.daily.map((r) => r.external.cost_coverage * 100),
              borderColor: '#f59e0b',
              backgroundColor: 'rgba(245,158,11,.1)',
              fill: true,
              pointRadius: 2,
              borderWidth: 2,
              tension: 0.25,
            },
            { label: t(BIZ.ca.chartFull), data: d.daily.map(() => 95), borderColor: '#10b981', borderDash: [5, 5], pointRadius: 0, borderWidth: 1 },
          ],
        },
        options: cov,
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, locale])

  // ------------------------------------------------------------------ 今日经营
  const tb = d?.today_business
  const tOverall = tb?.overall
  const tChat = tb?.conversation
  const tSub = tb?.moments_subsidy
  const tMargin = tOverall?.known_margin_units ?? 0
  const tCov = tOverall?.cost_coverage ?? 0
  let verdict: ReactNode = t(BIZ.ca.verdictLoading)
  let verdictCls = ''
  if (tb && tOverall) {
    if (!tOverall.requests) {
      verdict = t(BIZ.ca.verdictNone)
    } else {
      const dir = t(tMargin >= 0 ? BIZ.ca.profit : BIZ.ca.loss)
      if (tCov >= 0.95) {
        verdictCls = tMargin >= 0 ? 'profit' : 'loss'
        verdict = (
          <>
            <strong>{t(BIZ.ca.verdictOkHead, { dir, v: m(Math.abs(tMargin)) })}</strong> {t(BIZ.ca.verdictOkTail, { s: m(tSub?.known_upstream_cost_units), c: m(tChat?.known_margin_units) })}
          </>
        )
      } else {
        verdictCls = 'uncertain'
        verdict = (
          <>
            <strong>{t(BIZ.ca.verdictUncHead, { dir, v: m(Math.abs(tMargin)) })}</strong>{' '}
            {t(BIZ.ca.verdictUncTail, { p: ratePct(tCov), n: int(tOverall.unknown_cost_requests), s: m(tSub?.known_upstream_cost_units) })}
          </>
        )
      }
    }
  }

  const noteColor = coverage >= 0.95 ? 'var(--success-text)' : coverage >= 0.7 ? 'var(--warning-text)' : 'var(--error-text)'
  const preset = (n: number) => (days === n && applied.end === beijingDate(0) ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm')

  const section = (title: ReactNode, copy: ReactNode, exportBtn: ReactNode, body: ReactNode) => (
    <div className="ops-section-card">
      <div className="ops-section-head">
        <div>
          <div className="ops-section-title">{title}</div>
          <div className="ops-section-copy">{copy}</div>
        </div>
        {exportBtn}
      </div>
      {body}
    </div>
  )
  const exportBtn = (name: string, cols: Col[], rows: Row[]) => (
    <button className="btn btn-secondary btn-sm" onClick={() => exportCsv(name, cols, rows)}>
      {t(BIZ.ca.exportCsv)}
    </button>
  )

  const unattributedReq = d?.sources.find((s) => s.source === 'unattributed')?.requests ?? 0
  const attrRate = ext && ext.requests ? (ext.requests - unattributedReq) / ext.requests : 0

  return (
    <OpsPage
      eyebrow="COST INTELLIGENCE"
      title={t(A.nav.costAnalytics)}
      desc={t(BIZ.ca.desc)}
      chip={d ? `${d.start_date} ${t(BIZ.common.to)} ${d.end_date}` : load.error ? t(BIZ.ca.failed) : t(BIZ.ca.computing)}
    >
      <div className="ops-note" style={d ? { borderColor: noteColor } : undefined}>
        {!d || !ext ? (
          t(BIZ.ca.noteDefault)
        ) : coverage >= 0.95 ? (
          <>
            <strong>{t(BIZ.ca.noteGoodHead, { p: ratePct(coverage) })}</strong>
            {t(BIZ.ca.noteGoodTail, { n: int(ext.unknown_cost_requests) })}
          </>
        ) : (
          <>
            <strong>{t(BIZ.ca.noteBadHead)}</strong>
            {t(BIZ.ca.noteBadTail, { p: ratePct(coverage), n: int(ext.unknown_cost_requests), tok: compactTokens(ext.unknown_tokens), c: m(ext.unknown_user_charge_units) })}
          </>
        )}
      </div>
      {d?.payments_error && <Notice tone="danger">{t(BIZ.common.paymentsDown)}</Notice>}
      {!!load.error && !d && <Notice tone="danger">{errorText(load.error, locale)}</Notice>}

      <div className="ops-toolbar" style={{ marginBottom: 12 }}>
        <button className={preset(3)} onClick={() => setRange(3)}>
          {t(BIZ.ca.last3)}
        </button>
        <button className={preset(7)} onClick={() => setRange(7)}>
          {t(BIZ.ca.last7)}
        </button>
        <button className={preset(30)} onClick={() => setRange(30)}>
          {t(BIZ.ca.last30)}
        </button>
        <input type="date" style={FIELD} value={start} max={beijingDate(0)} onChange={(e) => setStart(e.target.value)} aria-label={t(BIZ.mu.start)} />
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(BIZ.common.to)}</span>
        <input type="date" style={FIELD} value={end} max={beijingDate(0)} onChange={(e) => setEnd(e.target.value)} aria-label={t(BIZ.mu.end)} />
        <button className="btn btn-primary btn-sm" onClick={query} disabled={load.loading}>
          {t(BIZ.ca.query)}
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-tertiary)' }}>{t(BIZ.ca.toolbarHint)}</span>
      </div>

      <div className="img-tabs" role="tablist" aria-label={t(BIZ.ca.tabsLabel)}>
        {(['overview', 'users', 'models'] as Tab[]).map((k) => (
          <button key={k} type="button" className={tab === k ? 'active' : ''} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {t(BIZ.ca.tab[k])}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div>
          <div className="ops-section-card ca-today-card">
            <div className="ops-section-head">
              <div>
                <div className="ops-section-title">
                  {t(BIZ.ca.todayTitle)} {H('todayResult')}
                </div>
                <div className="ops-section-copy">{t(BIZ.ca.todayCopy)}</div>
              </div>
              <small style={{ color: 'var(--text-tertiary)' }}>{tb ? t(BIZ.ca.todayAsOf, { d: tb.date }) : t(BIZ.ca.computing)}</small>
            </div>
            <div className={`ca-today-verdict ${verdictCls}`}>{verdict}</div>
            <Stats>
              <Stat
                label={<>{t(BIZ.ca.kTodayMargin)} {H('todayOverallMargin')}</>}
                value={tb ? <span style={{ color: signColor(tMargin) }}>{m(tMargin)}</span> : '—'}
                sub={tOverall ? t(tOverall.known_margin_rate === null ? BIZ.ca.kTodayMarginSubNA : BIZ.ca.kTodayMarginSub, { r: ratePct(tOverall.known_margin_rate), c: ratePct(tCov) }) : '—'}
              />
              <Stat
                label={<>{t(BIZ.ca.kChatRate)} {H('todayConversationRate')}</>}
                value={tChat ? <span style={{ color: tChat.known_margin_rate === null ? 'var(--text-tertiary)' : signColor(tChat.known_margin_rate) }}>{ratePct(tChat.known_margin_rate)}</span> : '—'}
                sub={tChat ? t(BIZ.ca.kChatRateSub, { c: m(tChat.known_user_charge_units), p: ratePct(tChat.cost_coverage) }) : '—'}
              />
              <Stat
                label={<>{t(BIZ.ca.kChatMargin)} {H('todayConversationMargin')}</>}
                value={tChat ? <span style={{ color: signColor(tChat.known_margin_units) }}>{m(tChat.known_margin_units)}</span> : '—'}
                sub={tChat ? t(BIZ.ca.kChatMarginSub, { c: m(tChat.known_upstream_cost_units), n: int(tChat.requests) }) : '—'}
              />
              <Stat
                label={<>{t(BIZ.ca.kSubsidy)} {H('todayMomentsSubsidy')}</>}
                value={tSub ? <span style={{ color: tSub.known_upstream_cost_units > 0 ? 'var(--error-text)' : undefined }}>{m(tSub.known_upstream_cost_units)}</span> : '—'}
                sub={tSub ? t(BIZ.ca.kSubsidySub, { n: int(tSub.requests), c: m(tSub.known_user_charge_units) }) : '—'}
              />
            </Stats>
            <div className="table-inner">
              <table>
                <thead>
                  <tr>
                    {todayCols.map((c) => (
                      <SortTh key={c.key} sortKey={c.key} state={sToday.state} onSort={sToday.toggle} help={c.help ? <> {H(c.help)}</> : undefined}>
                        {t(c.label)}
                      </SortTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sToday.sorted.length === 0 ? (
                    <tr>
                      <td colSpan={todayCols.length} className="empty">
                        {t(BIZ.common.loading)}
                      </td>
                    </tr>
                  ) : (
                    sToday.sorted.map((r) => (
                      <tr key={r.key as string}>
                        {todayCols.map((c) => (
                          <td key={c.key}>{c.cell(r)}</td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Stats id="caStatsA">
            <Stat label={<>{t(BIZ.ca.kCompany)} {H('companyCost')}</>} value={d ? m(d.summary.company_known_cost_units) : '—'} sub={t(BIZ.ca.kCompanySub)} />
            <Stat
              label={<>{t(BIZ.ca.kExternal)} {H('knownCost')}</>}
              value={ext ? m(ext.known_upstream_cost_units) : '—'}
              sub={ext ? t(BIZ.ca.kExternalSub, { u: int(ext.users), r: int(ext.requests) }) : '—'}
            />
            <Stat
              label={<>{t(BIZ.ca.kInternal)} {H('internalCost')}</>}
              value={intl ? m(intl.known_upstream_cost_units) : '—'}
              sub={intl ? t(BIZ.ca.kInternalSub, { p: ratePct(intl.cost_coverage), r: int(intl.requests) }) : '—'}
            />
            <Stat
              label={<>{t(BIZ.ca.kCoverage)} {H('coverage')}</>}
              value={ext ? <span style={{ color: coverageColor(coverage) }}>{ratePct(coverage)}</span> : '—'}
              sub={ext ? t(BIZ.ca.kCoverageSub, { n: int(ext.unknown_cost_requests) }) : '—'}
            />
          </Stats>
          <Stats id="caStatsB">
            <Stat label={<>{t(BIZ.ca.kCharge)} {H('userCharge')}</>} value={ext ? m(ext.user_charge_units) : '—'} sub={t(BIZ.ca.kChargeSub)} />
            <Stat
              label={<>{t(BIZ.ca.kMargin)} {H('margin')}</>}
              value={ext ? <span style={{ color: signColor(ext.known_margin_units) }}>{m(ext.known_margin_units)}</span> : '—'}
              sub={ext ? (ext.known_margin_rate === null ? t(BIZ.ca.kMarginNA) : t(BIZ.ca.kMarginSub, { r: ratePct(ext.known_margin_rate) })) : '—'}
            />
            <Stat label={<>{t(BIZ.ca.kFree)} {H('freeCost')}</>} value={ext ? m(ext.free_known_cost_units) : '—'} sub={ext ? t(BIZ.ca.kFreeSub, { n: int(ext.free_requests) }) : '—'} />
            <Stat
              label={<>{t(BIZ.ca.kPerOrg)} {H('perUserCost')}</>}
              value={ext ? m(ext.avg_known_cost_per_user_units) : '—'}
              sub={ext ? t(BIZ.ca.kPerOrgSub, { c: m(ext.avg_known_cost_per_1000_requests_units) }) : '—'}
            />
          </Stats>

          <div className="dashboard-chart-grid">
            <div className="dashboard-chart-card">
              <div className="chart-card-head">
                <span>
                  {t(BIZ.ca.cCost)} {H('costChart')}
                </span>
                <small>{t(BIZ.ca.cCostSub)}</small>
              </div>
              <div className="chart-h-220">{charts && <ChartCanvas config={charts.cost} label={t(BIZ.ca.cCost)} />}</div>
            </div>
            <div className="dashboard-chart-card">
              <div className="chart-card-head">
                <span>
                  {t(BIZ.ca.cCoverage)} {H('coverageChart')}
                </span>
                <small>{t(BIZ.ca.cCoverageSub)}</small>
              </div>
              <div className="chart-h-220">{charts && <ChartCanvas config={charts.coverage} label={t(BIZ.ca.cCoverage)} />}</div>
            </div>
          </div>

          {section(t(BIZ.ca.dailyTitle), t(BIZ.ca.dailyCopy), exportBtn('daily-cost', dailyCols, sDaily.sorted), table(dailyCols, sDaily, (r) => r.date as string, t(BIZ.common.noData)))}
        </div>
      )}

      {tab === 'users' && (
        <div>
          {section(
            t(BIZ.ca.usersTitle),
            t(BIZ.ca.usersCopy),
            exportBtn('org-cost-top100', userCols, sUsers.sorted),
            table(userCols, sUsers, (r) => r.account_id as string, t(BIZ.common.noData)),
          )}
          {section(
            t(BIZ.ca.unknownTitle),
            t(BIZ.ca.unknownCopy),
            exportBtn('unknown-risk-top50', unknownCols, sUnknown.sorted),
            table(unknownCols, sUnknown, (r) => r.account_id as string, t(BIZ.common.noData)),
          )}
        </div>
      )}

      {tab === 'models' && (
        <div>
          {section(
            t(BIZ.ca.modelsTitle),
            t(BIZ.ca.modelsCopy),
            exportBtn('model-provider-margin', modelCols, sModels.sorted),
            table(modelCols, sModels, (r) => `${r.provider_id as string}/${r.model_id as string}`, t(BIZ.common.noData)),
          )}
          {section(
            t(BIZ.ca.sourcesTitle),
            ext ? t(BIZ.ca.sourcesCopyRate, { p: ratePct(attrRate) }) : t(BIZ.ca.sourcesCopy),
            exportBtn('source-cost', sourceCols, sSources.sorted),
            table(sourceCols, sSources, (r) => r.source as string, t(BIZ.common.noData)),
          )}
        </div>
      )}
      <OpsNote>{t(BIZ.ca.footNote)}</OpsNote>
    </OpsPage>
  )
}
